import React, { useEffect, useMemo, useState } from "react";
import Close from "../../../../../images/Close.svg";
import { Title } from "../../../../../stories/Title/Title";
import { Button } from "../../../../../stories/Button/Button";
import { Label } from "../../../../../stories/Label/Label";
import useAssetTableJson from "../../../../../json/useAssetTableJson";
import { TableHeaderProps } from "../../../../../utils/types";

import {
  GridAlignment,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { ColumnVisibilityFor } from "../../../../../utils/Enum.Datagrid";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import { TABLE_ROW_HEIGHT } from "../../../../../styles/Variables";
import NoData from "../../../../../images/NoData.svg";
import {
  CustomModal,
  getModifiedScrollHeight,
} from "../../../../../utils/UtilFunctions";
import { ViewModalStyles } from "../../../../../styles/CustomModalStyles";
import ViewSupplier from "../supplier/view/Index";
import { CustomDrawer } from "../../../../../styles/CustomDrawerStyles";
import Assign from "./Assign";
import { selectedItemType } from "./Associate";
import { Checkbox } from "@mui/material";
import { useLazyQuery, useMutation } from "@apollo/client";

import useUrlParams from "../../../../../customHooks/useUrlParams";
import { GetStckItemLdgrAssociDetails } from "./queries/queries";
import {
  AtsStckItemLdgrAssoicEdge,
  AtsStckItemLdgrAssoicOrderField,
  DeleteItemLdgrAssociByItemResult,
  DeleteItemLdgrAssociByItemVar,
  GetStckItemLdgrAssociDetailsResult,
  GetStckItemLdgrAssociDetailsVar,
  ItemLdgrAssoicQueryType,
} from "./queries/types";
import { Operation } from "../../../../../utils/Enum.types";
import { DeleteItemLdgrAssociByItem } from "./queries/mutations";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import DeleteModal from "../../../../../customComponents/DeleteModal";
import { EMPTY_STRING, FETCH_MORE_DATA, ROWS_PER_PAGE, emptyMessage } from "../../../../../utils/constants";
import MessageModal from "../../../../../customComponents/MessageModal";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import LoadingModal from "../../../../pages/LoadingModal";
import { AtsStckItemOrderField, ItemQueryType } from "../items/queries/types";
import { GetItems } from "../items/queries/queries";
import { GetSuppliers } from "../supplier/queries/queries";
import { AtsAcLdgrOrderField, LdgrQueryType } from "../supplier/queries/types";
import useLabels from "../../../../../customHooks/useLabels";

interface Props {
  selectedItem: selectedItemType;
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const View = ({ selectedItem, setModalFlag }: Props) => {
  const { Masters_Table } = useAssetTableJson();
  const [viewModal, setViewModal] = useState(false);
  const [assign, setAssign] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const { user_details } = useLoggedInUserDetails();
 
  const { customer_id } = useUrlParams();
  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([]);
  const [edit, setEdit] = useState(false);
  const [message, setMessage] = useState(emptyMessage);

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [categories, setCategories] = useState<AtsStckItemLdgrAssoicEdge[]>([]);
  const [rows, setRows] = useState<GridValidRowModel[]>([]);

  const [getStckItemLdgrAssociDetails, { data, loading, fetchMore }] =
    useLazyQuery<
      GetStckItemLdgrAssociDetailsResult,
      GetStckItemLdgrAssociDetailsVar
    >(GetStckItemLdgrAssociDetails);

  const [deleteItemLdgrAssociByItem, { loading: deleteLoading }] = useMutation<
    DeleteItemLdgrAssociByItemResult,
    DeleteItemLdgrAssociByItemVar
  >(DeleteItemLdgrAssociByItem, {
    onError: (error) => {
      setMessage({
        flag: true,
        message: error.message,
        operation: Operation.DELETE,
      });
    },
  });

  const handleCheckboxChange = (id: number) => {
    setSelectedSuppliers(
      (prevSelected) =>
        prevSelected.includes(id)
          ? prevSelected.filter((itemId) => itemId !== id) // Remove if already selected
          : [...prevSelected, id] // Add if not selected
    );
  };

  const dynamicHeaders: TableHeaderProps[] =
    Masters_Table.Masters.Association.Assign.map((header) => ({
      headerName: header.headerName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      cellClassName: header.cellClassName,
      renderCell: (params) => {
        if (header.field === "id") {
          return (
            <>
              <Checkbox
                value={params.row}
                checked={selectedSuppliers.includes(params.row.supplierId)}
                onChange={() => handleCheckboxChange(params.row.supplierId)}
                inputProps={{ "aria-label": "controlled" }}
              />
            </>
          );
        }

        return params.value;
      },
    }));
  const columns: GridColDef[] = [...dynamicHeaders];

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    const savedModel = localStorage.getItem(ColumnVisibilityFor.VIEW_SUPPLIER);
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.VIEW_SUPPLIER,
      JSON.stringify(newModel)
    );
  };

  const handleClear = () => {
    setSelectedSuppliers([]);
  };

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges =
                fetchMoreResult.GetStckItemLdgrAssociDetails.edges;
              const pageInfo =
                fetchMoreResult.GetStckItemLdgrAssociDetails.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck =
                prevResult.GetStckItemLdgrAssociDetails.edges.filter(
                  ({ node: { id } }) =>
                    newEdges.findIndex(
                      ({ node: { id: newId } }) => newId === id
                    ) !== -1
                );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetStckItemLdgrAssociDetails: {
                  edges: [...categories, ...newEdges],
                  pageInfo,
                  totalCount: data
                    ? data.GetStckItemLdgrAssociDetails.totalCount!
                    : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && rows.length)
      scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rows]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetStckItemLdgrAssociDetails.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = categories.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setCategories(updatedNewData);
        setRows(
          updatedNewData.map(({ node }, index) => ({
            id: index + 1,
            supplierId: node.ldgr.id,
            desc: node.ldgr.desc,
            gstNo: node.ldgr.supplier_details.gstNo,
            city: node.ldgr.supplier_details.city,
            state: node.ldgr.supplier_details.state,
            proficiency: node.ldgr.supplier_details.proficiency,
            ldgrId: node.ldgr.id,
          }))
        );
      } else {
        setCategories(newData);
        setRows(
          newData.map(({ node }, index) => ({
            id: index + 1,
            supplierId: node.ldgr.id,
            desc: node.ldgr.desc,
            gstNo: node.ldgr.supplier_details.gstNo,
            city: node.ldgr.supplier_details.city,
            state: node.ldgr.supplier_details.state,
            proficiency: node.ldgr.supplier_details.proficiency,
            ldgrId: node.ldgr.id,
          }))
        );
      }
      setEndCursor(data.GetStckItemLdgrAssociDetails.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const fetchItem = async () => {
      try {
     

        if (selectedItem.itemId && customer_id) {
          await getStckItemLdgrAssociDetails({
            variables: {
              customer_id,
            
              input: {
                queryType: ItemLdgrAssoicQueryType.BY_ITEM_ID,
                id: selectedItem.itemId,
                stckCategoryID: 0,
                stckGroupID: 0,
                stckManufacturerID: 0,
                stckUomID: 0,
                stckSubCategoryID: 0,
              },
              name: "",
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsStckItemLdgrAssoicOrderField.LDGR_STATUS,
              },
            },
          });
        }
      } catch (error) {
        console.error((error as Error).message);
      }
    };
    fetchItem();
  }, [getStckItemLdgrAssociDetails, selectedItem, customer_id]);

  const HandleDelete = async () => {
    try {
     
      if ( customer_id) {
        setDeleteModal(!deleteModal);
        deleteItemLdgrAssociByItem({
          variables: {
            customer_id,
         
            user_details,
            itemId: selectedItem.itemId,
            ldgrIds: selectedSuppliers,
          },
          refetchQueries: [
            {
              query: GetStckItemLdgrAssociDetails,
              variables: {
                customer_id,
                
                input: {
                  queryType: ItemLdgrAssoicQueryType.BY_ITEM_ID,
                  id: selectedItem.itemId,
                  stckCategoryID: 0,
                  stckGroupID: 0,
                  stckManufacturerID: 0,
                  stckUomID: 0,
                  stckSubCategoryID: 0,
                },
                name: "",
                orderBy: {
                  direction: OrderDirection.ASC,
                  field: AtsStckItemLdgrAssoicOrderField.LDGR_STATUS,
                },
              },
            },
            {
              query:GetItems,
              variables: {
                after: null,
                customerID:customer_id ? customer_id : 0,
                companyID:0,
                first: ROWS_PER_PAGE,
                orderBy: {
                  direction: OrderDirection.ASC,
                  field: AtsStckItemOrderField.DESC,
                },
               
                name: EMPTY_STRING,
                hsnCode: "",
                input: {
                  query_type: ItemQueryType.All,
                  stckCategoryID: 0,
                  stckGroupID: 0,
                  stckManufacturerID: 0,
                  stckUomID: 0,
                  stckSubCategoryID: 0,
                  ldgrID: 0,
                  subsidiaryID:0
                },
              },
            },
            {
              query:GetSuppliers,
              variables: {
                after: null,
                customer_id,
                first: ROWS_PER_PAGE,
                orderBy: {
                  direction: OrderDirection.ASC,
                  field: AtsAcLdgrOrderField.CREATED_AT,
                },
                
                name: EMPTY_STRING,
                input: {
                  query_type: LdgrQueryType.LDGRS_NOT_ASSOIC_ITEM,
                  itemID: selectedItem.itemId ? selectedItem.itemId : 0,
                },
              },
            }
          ],
        }).then(({ data }) => {
          if (data && data.DeleteItemLdgrAssociByItem) {
            setMessage({
              flag: true,
              message: `${ledgerLabel} Deleted Sucessfully`,
              operation: Operation.DELETE,
            });
          }
        });
      }
    } catch (err) {
      console.error((err as Error).message);
    }
  };

  const handleClose = () => {
    handleClear();
    setMessage({
      message: "",
      flag: false,
      operation: Operation.NONE,
    });
  };
  const {itemLabel,ledgerLabel}=useLabels();

  return (
    <>
      <div className="assign-supplier">
        <div className="assign-supplier__title">
          <Title>View Associated {ledgerLabel} to {itemLabel} details</Title>
          <img src={Close} alt="" onClick={() => setModalFlag(false)} />
        </div>
        <div className="assign-supplier__header">
          <div className="assign-supplier__header--grid">
            <Label>{itemLabel} Description</Label>
            <span>:</span>
            <b>{selectedItem.itemDesc}</b>
            <Label>{itemLabel} Code</Label>
            <span>:</span>
            <span>{selectedItem.itemCode}</span>
          </div>
        </div>
        <Title variant="subtitle1">
          {deleteModal ? `Delete ${ledgerLabel}` : `Assign ${ledgerLabel}`}
        </Title>
        <div className="assign-supplier__tableblock">
          {rows.length ? (
            <StyledDatagrid
              columns={columns}
              rows={rows}
              rowHeight={TABLE_ROW_HEIGHT}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityChange}
              initialState={{
                columns: {
                  columnVisibilityModel: columnVisibilityModel,
                },
              }}
              onCellClick={(params) => {
                if (params.field === "supplier_name") {
                  setViewModal(!viewModal);
                }
              }}
              disableRowSelectionOnClick
              hideFooter
            />
          ) : (
            <>
              <img src={NoData} alt="" className="nodata-image" />
            </>
          )}
        </div>
        <div className="row g-0">
          <div className="col">
            {deleteModal ? (
              <>
                <Button mode="save" />
                <Button mode="clear" onClick={handleClear} />
              </>
            ) : (
              <>
                <Button
                  mode="assign"
                  onClick={() => {
                    setEdit(true);
                    setAssign(!assign);
                  }}
                />
                <Button
                  mode="delete"
                  onClick={() => {
                    setDeleteModal(!deleteModal);
                  }}
                  disabled={!selectedSuppliers.length}
                />
              </>
            )}

            <Button mode="cancel" onClick={() => setModalFlag(false)} />
          </div>
          <div className="col-4 flex-end">
            <div className="overall-total-count">
              Selected {ledgerLabel} <b>{selectedSuppliers.length}</b>
            </div>
          </div>
        </div>
      </div>
      <CustomModal
        isOpen={viewModal}
        onRequestClose={() => setViewModal(!viewModal)}
        contentComponent={<ViewSupplier setModalFlag={setViewModal} />}
        style={ViewModalStyles}
      />
      <CustomDrawer
        anchor="right"
        open={assign}
        onClose={() => setAssign(!assign)}
      >
        <Assign
          selectedItem={selectedItem}
          setModalFlag={setAssign}
        />
      </CustomDrawer>

      <DeleteModal
        id={0}
        modalFlag={deleteModal}
        setModalFlag={setDeleteModal}
        handleDelete={HandleDelete}
      />

      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
      <LoadingModal flag={loading || deleteLoading} />
    </>
  );
};

export default View;
